import { useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import WhatsAppFAB from "@/components/site/WhatsAppFAB";
import { BLOG_POSTS, findPostBySlug } from "@/data/blog";
import { BRAND } from "@/data/products";
import {
  ArrowLeft,
  ArrowUpRight,
  Calendar,
  Clock,
  CheckCircle2,
} from "lucide-react";

// Turns **bold** markers into <strong> and *italic* markers into <em>
function renderInline(text) {
  const nodes = [];
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  parts.forEach((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      nodes.push(
        <strong key={i} className="text-ink font-semibold">
          {p.slice(2, -2)}
        </strong>
      );
    } else if (p.startsWith("*") && p.endsWith("*") && p.length > 2) {
      nodes.push(
        <em key={i} className="italic">
          {p.slice(1, -1)}
        </em>
      );
    } else if (p) {
      nodes.push(<span key={i}>{p}</span>);
    }
  });
  return nodes;
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = useMemo(() => findPostBySlug(slug), [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    if (post) {
      document.title = `${post.title} | Vardaan Arts Journal`;
      const meta = document.querySelector('meta[name="description"]');
      if (meta && post.metaDescription) {
        meta.setAttribute("content", post.metaDescription);
      }
    }
  }, [post]);

  if (!post) {
    return (
      <div className="min-h-screen bg-cream-100 flex items-center justify-center px-6">
        <div className="text-center">
          <h1 className="font-serif text-4xl text-ink">Article not found</h1>
          <Link
            to="/blog"
            className="mt-6 inline-flex items-center gap-2 text-[12px] tracking-[0.28em] uppercase text-gold-dark"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Journal
          </Link>
        </div>
      </div>
    );
  }

  const related = BLOG_POSTS.filter((p) => p.slug !== post.slug).slice(0, 2);

  return (
    <div className="min-h-screen bg-cream-100 text-ink" data-testid="blog-post-page">
      <Header />
      <main className="pt-28 md:pt-36 pb-24 md:pb-32">
        {/* Hero */}
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center mb-12 md:mb-16">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-gold-dark hover:text-gold-deeper mb-8"
            data-testid="back-to-blog"
          >
            <ArrowLeft className="w-3 h-3" /> Back to Journal
          </Link>
          <div className="text-[11px] tracking-[0.32em] uppercase text-gold-dark mb-5 font-medium">
            {post.kicker ? `Blog ${post.number} · ${post.kicker}` : "The Vardaan Journal"}
          </div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ink leading-[1.06]"
          >
            {post.title}
          </motion.h1>
          <div className="flex items-center justify-center gap-6 mt-8 text-[11px] tracking-[0.2em] uppercase text-ink-soft">
            <span className="flex items-center gap-2">
              <Calendar className="w-3 h-3" />
              {new Date(post.date).toLocaleDateString("en-IN", {
                day: "numeric",
                month: "short",
                year: "numeric",
              })}
            </span>
            <span className="flex items-center gap-2">
              <Clock className="w-3 h-3" />
              {post.readTime}
            </span>
          </div>
        </div>

        {/* Cover image */}
        <div className="max-w-5xl mx-auto px-6 md:px-10 mb-14 md:mb-20">
          <div className="relative overflow-hidden aspect-[16/9] bg-cream-200 border border-gold/25">
            <img
              src={post.coverImage}
              alt={post.title}
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* Content */}
        <article className="max-w-3xl mx-auto px-6 md:px-10 space-y-6 md:space-y-7">
          {post.content.map((block, i) => (
            <ContentBlock key={i} block={block} />
          ))}
        </article>

        {/* Tags */}
        <div className="max-w-3xl mx-auto px-6 md:px-10 mt-14">
          <div className="flex flex-wrap gap-2">
            {post.tags.map((t) => (
              <span
                key={t}
                className="text-[10px] tracking-[0.22em] uppercase text-gold-dark border border-gold/40 px-3 py-1"
              >
                {t}
              </span>
            ))}
          </div>
        </div>

        {/* Related */}
        {related.length > 0 && (
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 mt-24 md:mt-32">
            <div className="text-[11px] tracking-[0.32em] uppercase text-gold-dark mb-4 font-medium text-center">
              — Continue Reading
            </div>
            <h2 className="font-serif text-3xl md:text-4xl text-ink text-center mb-12">
              More from the <span className="italic gold-text">Vardaan Journal</span>
            </h2>
            <div className="grid md:grid-cols-2 gap-6 md:gap-8">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/blog/${r.slug}`}
                  className="group block bg-cream-100 border border-gold/25 hover:border-gold transition-colors overflow-hidden"
                  data-testid={`related-post-${r.slug}`}
                >
                  <div className="relative overflow-hidden aspect-[16/10] bg-cream-200">
                    <img
                      src={r.coverImage}
                      alt={r.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                    />
                  </div>
                  <div className="p-6">
                    <div className="text-[10px] tracking-[0.24em] uppercase text-gold-dark mb-2">
                      {r.kicker || r.tags[0]}
                    </div>
                    <h3 className="font-serif text-2xl text-ink leading-tight group-hover:text-gold-dark transition-colors">
                      {r.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </main>
      <Footer />
      <WhatsAppFAB />
    </div>
  );
}

function ContentBlock({ block }) {
  switch (block.type) {
    case "p":
      return (
        <p className="text-[17px] md:text-[18px] leading-[1.75] text-ink-soft">
          {renderInline(block.text)}
        </p>
      );

    case "h2":
      return (
        <h2 className="font-serif text-3xl md:text-[36px] text-ink mt-10 md:mt-14 leading-tight border-l-2 border-gold pl-5">
          {block.text}
        </h2>
      );

    case "list":
      return (
        <ul className="space-y-3 pl-1">
          {block.items.map((it, i) => (
            <li
              key={i}
              className="flex items-start gap-3 text-[17px] leading-[1.7] text-ink-soft"
            >
              <CheckCircle2 className="w-5 h-5 text-gold-dark flex-shrink-0 mt-0.5" />
              <span>{renderInline(it)}</span>
            </li>
          ))}
        </ul>
      );

    case "quote":
      return (
        <blockquote className="border-l-2 border-gold pl-6 py-2 my-6 font-serif italic text-2xl md:text-[28px] leading-snug text-ink">
          "{block.text}"
        </blockquote>
      );

    case "callout":
      return (
        <div className="bg-cream-200/60 border border-gold/40 p-6 md:p-8 my-4">
          <div className="text-[10px] tracking-[0.32em] uppercase text-gold-dark mb-3 font-medium">
            {block.title}
          </div>
          <p className="text-[17px] leading-[1.7] text-ink">
            {renderInline(block.text)}
          </p>
        </div>
      );

    case "cta":
      return (
        <div className="bg-ink text-cream-100 p-8 md:p-10 my-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="font-serif text-2xl md:text-[28px] leading-tight">
            {renderInline(block.text)}
          </div>
          <a
            href={block.buttonHref || "#contact"}
            className="inline-flex items-center gap-2 px-6 py-3 bg-gold text-ink text-[11px] tracking-[0.28em] uppercase hover:bg-gold-light transition-colors flex-shrink-0 whitespace-nowrap"
          >
            {block.buttonLabel}
            <ArrowUpRight className="w-4 h-4" />
          </a>
        </div>
      );

    case "table":
      return (
        <div className="overflow-x-auto my-4 border border-gold/30">
          <table className="w-full text-left border-collapse">
            <thead className="bg-ink text-cream-100">
              <tr>
                {block.headers.map((h, i) => (
                  <th
                    key={i}
                    className="p-4 text-[11px] tracking-[0.2em] uppercase font-medium border-r border-gold/30 last:border-r-0"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, ri) => (
                <tr
                  key={ri}
                  className={`${
                    ri % 2 === 0 ? "bg-cream-100" : "bg-cream-200/40"
                  }`}
                >
                  {row.map((cell, ci) => (
                    <td
                      key={ci}
                      className={`p-4 text-[15px] leading-[1.6] border-r border-gold/20 last:border-r-0 border-t border-gold/20 ${
                        ci === 0
                          ? "font-serif text-[17px] text-ink font-semibold"
                          : "text-ink-soft"
                      }`}
                    >
                      {renderInline(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    case "image":
      return (
        <figure className="my-6">
          <div className="relative overflow-hidden border border-gold/30 bg-cream-200">
            <img
              src={block.src}
              alt={block.caption || "Vardaan Arts process"}
              loading="lazy"
              className="w-full h-auto object-cover"
              data-testid="blog-inline-image"
            />
          </div>
          {block.caption && (
            <figcaption className="text-center text-[11px] tracking-[0.22em] uppercase text-ink-soft mt-3">
              {block.caption}
            </figcaption>
          )}
        </figure>
      );

    case "video":
      return (
        <figure className="my-8">
          <div className="relative">
            <div className="absolute inset-0 -m-3 border border-gold/30 pointer-events-none" />
            <div className="relative overflow-hidden bg-black border border-gold/40">
              <video
                src={block.src}
                poster={block.poster}
                controls
                preload="metadata"
                playsInline
                className="w-full aspect-[4/5] sm:aspect-video object-cover"
                data-testid="blog-video"
              />
            </div>
          </div>
          {block.caption && (
            <figcaption className="text-center text-[11px] tracking-[0.22em] uppercase text-ink-soft mt-5">
              {block.caption}
            </figcaption>
          )}
        </figure>
      );

    case "step":
      return (
        <div className="bg-cream-200/40 border-l-4 border-gold p-6 md:p-8 my-4">
          <div
            className={
              block.image
                ? "grid md:grid-cols-[1fr_240px] gap-7 items-start"
                : ""
            }
          >
            <div>
              <div className="text-[11px] tracking-[0.32em] uppercase text-gold-dark mb-2 font-semibold">
                Step {block.number}
              </div>
              <h3 className="font-serif text-2xl md:text-[30px] text-ink leading-tight mb-3">
                {block.title}
              </h3>
              <p className="text-[16px] leading-[1.7] text-ink-soft mb-4">
                {renderInline(block.intro)}
              </p>
              <ul className="space-y-2">
                {block.points.map((pt, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 text-[16px] leading-[1.65] text-ink-soft"
                  >
                    <span className="text-gold-dark mt-1">•</span>
                    <span>{renderInline(pt)}</span>
                  </li>
                ))}
              </ul>
            </div>
            {block.image && (
              <div className="relative overflow-hidden border border-gold/40 bg-cream-100 aspect-[3/4] rounded-t-[50%_22%] mt-2 md:mt-0">
                <img
                  src={block.image}
                  alt={`Step ${block.number} — ${block.title}`}
                  loading="lazy"
                  className="w-full h-full object-cover"
                  data-testid={`step-image-${block.number}`}
                />
              </div>
            )}
          </div>
        </div>
      );

    default:
      return null;
  }
}
